import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Moon,
  Zap,
  User,
  HardHat,
  Lightbulb,
  ArrowRight,
  CheckCircle,
  Compass,
  Route,
  Crown,
  Package,
  ExternalLink,
  Send,
  Backpack,
  Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEOHead from '../components/SEOHead';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const WalkingTipsPage = () => {
  const [tipSubmission, setTipSubmission] = useState({
    title: '',
    description: '',
    category: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleTipSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/tips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: tipSubmission.title,
          category: tipSubmission.category,
          tip: tipSubmission.description,
          email: tipSubmission.email || null,
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        setTipSubmission({ title: '', description: '', category: '', email: '' });
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch {
      alert('Could not connect to the server. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <SEOHead
        title="Walking Tips & Guides - Wanderloop"
        description="Essential walking tips to help you walk smarter, wilder, and find your loop"
      />
      <Header />

      {/* Hero Illustration Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white py-20 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-amber-400/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-orange-400/10 rounded-full blur-xl"></div>
          <div className="absolute top-40 right-1/4 w-24 h-24 bg-yellow-400/10 rounded-full blur-lg"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 text-center">
          {/* Main illustration */}
          <div className="mb-12">
            <div className="relative inline-block">
              <div className="w-80 h-60 bg-gradient-to-br from-slate-800 to-blue-900 rounded-3xl shadow-2xl mx-auto relative overflow-hidden">
                {/* Illustrated landscape background */}
                <div className="absolute inset-0 bg-gradient-to-b from-slate-700 to-blue-800 opacity-70"></div>

                {/* Walking path */}
                <div className="absolute bottom-0 left-0 right-0">
                  <svg viewBox="0 0 320 120" className="w-full h-20 text-amber-500/30">
                    <path d="M0,120 L80,90 L160,100 L240,70 L320,80 L320,120 Z" fill="currentColor" />
                  </svg>
                </div>

                {/* Walker figure */}
                <div className="absolute top-12 left-1/2 transform -translate-x-1/2">
                  <div className="w-20 h-20 bg-amber-500 rounded-2xl flex items-center justify-center shadow-lg relative">
                    <User className="w-8 h-8 text-white" />
                    {/* Walking motion indicator */}
                    <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 flex gap-1">
                      <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-white/80 rounded-full animate-bounce delay-200"></div>
                      <div className="w-2 h-2 bg-white rounded-full animate-bounce delay-400"></div>
                    </div>
                  </div>
                </div>

                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-6 h-6 bg-amber-400 rounded-full shadow-lg animate-bounce">
                  <Lightbulb className="w-4 h-4 text-white m-1" />
                </div>
                <div className="absolute bottom-8 left-8 w-4 h-4 bg-orange-400 rounded-full shadow-lg animate-bounce delay-300">
                  <Compass className="w-3 h-3 text-white m-0.5" />
                </div>
                <div className="absolute top-16 left-16 w-5 h-5 bg-yellow-400 rounded-full shadow-lg animate-bounce delay-500">
                  <Route className="w-3 h-3 text-white m-1" />
                </div>
              </div>

              {/* Tips badge */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full p-3 shadow-xl">
                <Lightbulb className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-black text-white mb-6">
            Walk smarter. Walk wilder.
          </h1>

          <p className="text-xl text-blue-100 mb-12 max-w-3xl mx-auto leading-relaxed">
            From urban loops to wild trails, these essential tips will help <br />
            you make the most of every step.
          </p>
        </div>
      </section>

      {/* Night Walk Ready Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-3xl p-8 md:p-12 text-black relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-32 translate-x-32"></div>

            <div className="relative z-10 flex items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <Moon className="w-8 h-8 text-black" />
                </div>
              </div>

              <div className="flex-1">
                <h2 className="text-3xl font-black mb-4">Night Walk Ready?</h2>
                <p className="text-xl text-black/80 leading-relaxed">
                  Bring a headlamp, wear reflective gear, and pick routes you know well.
                  The world changes after dark—walk with wonder and awareness.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Tips Sections */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            {/* Section 1: Planning Your Walk */}
            <Card className="bg-white shadow-lg hover:shadow-xl transition-all duration-300 border-0">
              <CardHeader className="pb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <Badge variant="outline" className="text-amber-600 border-amber-200 mb-2">Section 1</Badge>
                    <CardTitle className="text-xl font-black text-gray-900">PLANNING YOUR WALK</CardTitle>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Start with Intention</h4>
                      <p className="text-sm text-gray-600">
                        What's your hope—reflection, discovery, sunglasses, pick story
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Use Wanderloop filters</h4>
                      <p className="text-sm text-gray-600">
                        Sort by distance, terrain, scenery and theme
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Know the weather & terrain</h4>
                      <p className="text-sm text-gray-600">
                        Layers are your friend, <br />
                        Dry feet = happy walker.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-3">Quick checklist</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-amber-500" />
                      <span>Check the weather and trail conditions</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-amber-500" />
                      <span>Tell someone your route and ETA</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-amber-500" />
                      <span>Charge your phone before you go</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-amber-500" />
                      <span>Pick a walk that matches your energy level</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 2: What to Wear & Pack */}
            <Card className="bg-white shadow-lg hover:shadow-xl transition-all duration-300 border-0">
              <CardHeader className="pb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                    <Backpack className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <Badge variant="outline" className="text-amber-600 border-amber-200 mb-2">Section 2</Badge>
                    <CardTitle className="text-xl font-black text-gray-900">WHAT TO WEAR & PACK</CardTitle>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Footwear first</h4>
                      <p className="text-sm text-gray-600">
                        Supportive shoes with good grip. Break them in before a long walk—blisters end adventures early.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Dress in layers</h4>
                      <p className="text-sm text-gray-600">
                        Mornings are cool, midday is warm. A light layer you can tie around your waist beats overdressing every time.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Water & sunscreen</h4>
                      <p className="text-sm text-gray-600">
                        Bring more water than you think you need. Apply sunscreen before you leave—reapply if you're out more than an hour.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Small daypack essentials</h4>
                      <p className="text-sm text-gray-600">
                        Phone, water bottle, snack, sunglasses, and a hat. That's it—keep it light and keep moving.
                      </p>
                    </div>
                  </div>
                </div>

                <Link to="/gear">
                  <Button variant="outline" className="w-full mt-4 border-amber-200 text-amber-600 hover:bg-amber-50">
                    <Package className="w-4 h-4 mr-2" />
                    EXPLORE OUR GEAR PAGE
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Section 3: How to Walk More Mindfully */}
            <Card className="bg-white shadow-lg hover:shadow-xl transition-all duration-300 border-0">
              <CardHeader className="pb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <Badge variant="outline" className="text-amber-600 border-amber-200 mb-2">Section 3</Badge>
                    <CardTitle className="text-xl font-black text-gray-900">HOW TO WALK MORE MINDFULLY</CardTitle>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm text-gray-600">
                        Walk the first 5 minutes in silence
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm text-gray-600">
                        Look up—not just at feet
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm text-gray-600">
                        Pause, Let something surprise
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm text-gray-600">
                        Write or voice memo thoughts mid-walk
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm text-gray-600">
                        Try a walking mantra (e.g. "step, breathe, notice, smile")
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 4: Documenting Your Walk */}
            <Card className="bg-white shadow-lg hover:shadow-xl transition-all duration-300 border-0">
              <CardHeader className="pb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                    <Camera className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <Badge variant="outline" className="text-amber-600 border-amber-200 mb-2">Section 4</Badge>
                    <CardTitle className="text-xl font-black text-gray-900">DOCUMENTING YOUR WALK</CardTitle>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Photograph with intention</h4>
                      <p className="text-sm text-gray-600">
                        Take one great photo instead of twenty okay ones. Look for light, texture, and the details most people walk past.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Voice memos over typing</h4>
                      <p className="text-sm text-gray-600">
                        Record a 30-second voice note when something strikes you. You'll capture tone and energy that text can't hold.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Keep a walking journal</h4>
                      <p className="text-sm text-gray-600">
                        Even a few sentences after each walk builds a personal archive. Date, route, one thing you noticed—that's enough.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Share your route</h4>
                      <p className="text-sm text-gray-600">
                        Found a great walk? Submit it to Wanderloop so others can discover it too. The best walks come from the community.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 5: Safety & Etiquette */}
            <Card className="bg-white shadow-lg hover:shadow-xl transition-all duration-300 border-0 lg:col-span-2">
              <CardHeader className="pb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                    <HardHat className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <Badge variant="outline" className="text-amber-600 border-amber-200 mb-2">Section 5</Badge>
                    <CardTitle className="text-xl font-black text-gray-900">SAFETY & ETIQUETTE</CardTitle>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Add a walk loop to your commute
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Start a weekly "walk club" or lunch loop
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Wander without a destination—just follow curiosity
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Try walking meetings (headphones + rhythm = big ideas)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bonus Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-3xl p-8 md:p-12 text-black relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl -translate-y-24 -translate-x-24"></div>

            <div className="relative z-10">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Crown className="w-8 h-8 text-black" />
              </div>

              <h2 className="text-3xl font-black mb-4">
                Bonus: HOW TO CREATE A SIGNATURE WALK
              </h2>
              <p className="text-xl text-black/70 mb-8 leading-relaxed">
                Share your favorite route with the Wanderloop community.
              </p>

              <Link to="/submit">
                <Button
                  size="lg"
                  className="bg-white text-amber-600 hover:bg-amber-50 font-semibold px-8 py-3 rounded-full shadow-lg"
                >
                  Submit Your Walk
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Submit a Tip Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIzIi8+PC9nPjwvZz48L3N2Zz4=')]"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Send className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-black mb-4">SUBMIT A TIP</h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
              Have a walking tip that changed your experience? Share it with the community!
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-yellow-400" />
                </div>
                <h3 className="text-2xl font-bold mb-3">Thank you!</h3>
                <p className="text-blue-100 text-lg mb-6">Your tip has been submitted. We'll review it soon.</p>
                <Button
                  onClick={() => setSubmitted(false)}
                  className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-500 hover:to-orange-600 font-semibold px-6 py-2 rounded-full"
                >
                  Submit Another Tip
                </Button>
              </div>
            ) : (
              <form onSubmit={handleTipSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Tip Title</label>
                    <Input
                      type="text"
                      placeholder="Give your tip a catchy title..."
                      value={tipSubmission.title}
                      onChange={(e) => setTipSubmission({...tipSubmission, title: e.target.value})}
                      className="bg-white/10 border-white/20 text-white placeholder-white/60 focus:border-yellow-400/60"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Category</label>
                    <select
                      value={tipSubmission.category}
                      onChange={(e) => setTipSubmission({...tipSubmission, category: e.target.value})}
                      className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-yellow-400/60 focus:outline-none"
                      required
                    >
                      <option value="">Select a category...</option>
                      <option value="planning">Planning</option>
                      <option value="mindfulness">Mindfulness</option>
                      <option value="safety">Safety & Etiquette</option>
                      <option value="gear">Gear & Equipment</option>
                      <option value="night">Night Walking</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Your Tip</label>
                  <Textarea
                    placeholder="Share your walking wisdom..."
                    value={tipSubmission.description}
                    onChange={(e) => setTipSubmission({...tipSubmission, description: e.target.value})}
                    className="bg-white/10 border-white/20 text-white placeholder-white/60 focus:border-yellow-400/60 min-h-[120px]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Email (optional)</label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={tipSubmission.email}
                    onChange={(e) => setTipSubmission({...tipSubmission, email: e.target.value})}
                    className="bg-white/10 border-white/20 text-white placeholder-white/60 focus:border-yellow-400/60"
                  />
                  <p className="text-sm text-blue-100 mt-1">We'll credit you if we feature your tip!</p>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-500 hover:to-orange-600 font-semibold py-4 rounded-full shadow-lg text-lg"
                >
                  {submitting ? 'Submitting...' : 'Submit Your Tip'}
                  {!submitting && <Send className="w-5 h-5 ml-2" />}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
};

export default WalkingTipsPage;
